import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import type { DetailedCircuitGeometry, TrackContributionGpsCoord } from '@kangaroo/shared';
import { isContinuousTelemetryPair, splitContinuousTelemetryPoints } from '@kangaroo/shared';

import { getGoogleMapsMapId } from '../../lib/runtimeConfig';
import {
  trackCatalogSegmentColor,
  trackCatalogSegmentVisible,
  type TrackCatalogMapLayers,
} from '../../lib/trackCatalog';
import {
  loadGoogleMaps,
  mapsAuthHasFailed,
  mapsDevAuthHint,
  onMapsAuthFailure,
  registerGoogleMapsAuthFailureHandler,
} from '../../lib/mapsLoader';
import type { ReferenceTrackGuideColors } from '../../lib/osmTrackOverlay';
import {
  buildReplayMotionCache,
  interpolateMapPointAtTime,
  interpolateMapPointAtTimeWithHint,
  sampleReplayMotionCache,
  type InterpolateMapPointHint,
  type ReplayMotionCache,
  speedColorBrakeToFlatOut,
  trackSpeedRange,
  type TrackMapChannelOverlay,
  type MapReplayCursor,
  type TrackMapMarker,
  type TrackMapPoint,
} from '../../lib/telemetrySession';
import { subscribeToPlayheadTicks } from '../../lib/useTelemetryReplay';
import { TrackMapLegend, type TrackMapLegendItem } from './TrackMapLegend';

type SpeedRange = {
  min: number;
  max: number;
};

type GoogleMapsTrackMapProps = {
  apiKey: string;
  points: TrackMapPoint[];
  trackGroups?: TrackMapPoint[][];
  referenceTrackGroups?: TrackMapPoint[][];
  speedRange?: SpeedRange;
  idealPoints?: TrackMapPoint[];
  showIdealLine?: boolean;
  showLapLines?: boolean;
  channelOverlays?: TrackMapChannelOverlay[];
  height?: number;
  title?: string;
  showLegend?: boolean;
  legendTrackItems?: TrackMapLegendItem[];
  fill?: boolean;
  cursorTimecodeMs?: number | null;
  replayCursors?: MapReplayCursor[];
  followReplayCursor?: boolean;
  resolveReplayCursorTimecodeMsRef?: MutableRefObject<
    ((cursor: MapReplayCursor) => number | null) | undefined
  >;
  resetViewKey?: string | number;
  onMapClick?: () => void;
  onMapLatLngClick?: (coord: { lat: number; lon: number }) => void;
  onMapLatLngHover?: (coord: { lat: number; lon: number } | null) => void;
  highlightLines?: Array<{
    id: string;
    points: Array<{ lat: number; lon: number }>;
    color?: string;
    width?: number;
    opacity?: number;
    dash?: number[];
  }>;
  /** Drag on the map to add centerline points (disables pan while the mouse button is held). */
  freehandDrawActive?: boolean;
  onMapLatLngDrawStroke?: (coord: { lat: number; lon: number }) => void;
  onMapMarkerClick?: (markerId: string) => void;
  onMapMarkerDragEnd?: (markerId: string, coord: { lat: number; lon: number }) => void;
  mapHoverMarker?: { lat: number; lon: number };
  startFinishLine?: { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } };
  crossTrackLines?: Array<{
    id: string;
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
    color?: string;
  }>;
  mapMarkers?: TrackMapMarker[];
  mapMarkerScale?: number;
  mapMarkerLightOutline?: boolean;
  heatLineWidth?: number;
  heatLineHaloWidth?: number;
  heatLineWhiteHalo?: boolean;
  fitBoundsPadding?: number;
  maxFitZoom?: number;
  minFitZoom?: number;
  sectorHighlightGroups?: TrackMapPoint[][];
  selectedSectorIndex?: number | null;
  onTilesReady?: () => void;
  /** Center the map when no GPS trace is available (manual layout mode). */
  mapViewport?: { lat: number; lon: number; zoom?: number };
  /** When false, keep the user's pan/zoom while overlays update. */
  autoFitBounds?: boolean;
  referenceTrackStyle?: 'default' | 'guide';
  /** Per-group dash/arrow colors when `referenceTrackStyle` is `guide`. */
  referenceTrackGuideColors?: ReferenceTrackGuideColors[];
  /** Direction chevrons on guide-style reference overlays (off for Circuit Maker references). */
  referenceTrackGuideArrows?: boolean;
  /** Catalog-style corridor preview with straights, corners, and outline. */
  detailedCircuitPreview?: DetailedCircuitGeometry | null;
  /** Live corridor outline while drawing (before lap sections are finalized). */
  corridorOutlineRings?: TrackContributionGpsCoord[][];
};

type LatLngSegment = {
  start: google.maps.LatLngLiteral;
  end: google.maps.LatLngLiteral;
  color: string;
};

type ColoredPath = {
  color: string;
  path: google.maps.LatLngLiteral[];
};

type AdvancedMarker = google.maps.marker.AdvancedMarkerElement;
type MapOverlay = google.maps.MVCObject | AdvancedMarker;
type CursorMarker = google.maps.Marker | AdvancedMarker;
const REPLAY_CURSOR_Z_INDEX = 48;

function canUseAdvancedMarkers() {
  return Boolean(getGoogleMapsMapId() && google.maps.marker?.AdvancedMarkerElement);
}

function extendBoundsWithMapMarkers(bounds: google.maps.LatLngBounds, markers: TrackMapMarker[]) {
  for (const marker of markers) {
    bounds.extend({ lat: marker.lat, lng: marker.lon });
  }
}

function clearMapOverlay(overlay: MapOverlay) {
  if ('setMap' in overlay && typeof overlay.setMap === 'function') {
    overlay.setMap(null);
    return;
  }

  if ('map' in overlay) {
    overlay.map = null;
  }
}

function latLngFromPoint(point: TrackMapPoint): google.maps.LatLngLiteral {
  return { lat: point.lat, lng: point.lon };
}

function trackPathFromPoints(points: TrackMapPoint[]) {
  return points.map(latLngFromPoint);
}

/** High-contrast speed bands for satellite imagery (brake → accel). */
function speedColorSatellite(speedKph: number, minSpeed: number, maxSpeed: number) {
  return speedColorBrakeToFlatOut(speedKph, minSpeed, maxSpeed);
}

function latLngSegments(points: TrackMapPoint[], speedRange?: SpeedRange): LatLngSegment[] {
  if (points.length < 2) {
    return [];
  }

  const speeds = points.map((point) => point.speedKph);
  const minSpeed = speedRange?.min ?? Math.min(...speeds);
  const maxSpeed = speedRange?.max ?? Math.max(...speeds);

  return points.slice(1).flatMap((point, index) => {
    const previous = points[index]!;
    if (!isContinuousTelemetryPair(previous, point)) return [];
    const speed = (previous.speedKph + point.speedKph) / 2;
    return [
      {
        start: latLngFromPoint(previous),
        end: latLngFromPoint(point),
        color: speedColorSatellite(speed, minSpeed, maxSpeed),
      },
    ];
  });
}

/** Merge adjacent same-color segments into continuous polylines (avoids chunky gaps). */
function mergeColoredPaths(segments: LatLngSegment[]): ColoredPath[] {
  const paths: ColoredPath[] = [];

  for (const segment of segments) {
    const last = paths.at(-1);
    if (last && last.color === segment.color) {
      last.path.push(segment.end);
      continue;
    }

    paths.push({ color: segment.color, path: [segment.start, segment.end] });
  }

  return paths;
}

function addPolyline(
  map: google.maps.Map,
  overlays: MapOverlay[],
  path: google.maps.LatLngLiteral[],
  options: google.maps.PolylineOptions,
) {
  if (path.length < 2) {
    return;
  }

  overlays.push(
    new google.maps.Polyline({
      map,
      path,
      geodesic: false,
      ...options,
      clickable: false,
    }),
  );
}

function advancedMarkerContent(options: {
  color: string;
  label?: string;
  size: number;
  selected?: boolean;
  lightOutline?: boolean;
  icon?: 'video';
}) {
  const outline = options.lightOutline === false ? 'rgba(7,17,28,0.72)' : '#fff';
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '4px';
  wrap.style.transform = 'translate(-50%, -50%)';
  wrap.style.pointerEvents = 'auto';

  const dot = document.createElement('span');
  dot.style.width = `${options.size}px`;
  dot.style.height = `${options.size}px`;
  dot.style.borderRadius = '999px';
  dot.style.background = options.color;
  dot.style.border = `${options.selected ? 3 : 2}px solid ${outline}`;
  dot.style.boxShadow = '0 0 0 2px rgba(7,17,28,0.9), 0 8px 18px rgba(0,0,0,0.38)';
  if (options.icon === 'video') {
    dot.style.position = 'relative';
    dot.style.display = 'inline-flex';
    dot.style.alignItems = 'center';
    dot.style.justifyContent = 'center';
    dot.innerHTML = `
      <svg width="${Math.max(12, options.size * 0.62)}" height="${Math.max(12, options.size * 0.62)}" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17 10.5V6c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-4.5l4 4v-11l-4 4ZM15 17H5V7h10v10Z" fill="#020A12"/>
      </svg>
    `;
  }
  wrap.appendChild(dot);

  if (options.label) {
    const label = document.createElement('span');
    label.textContent = options.label;
    label.style.padding = '2px 5px';
    label.style.borderRadius = '999px';
    label.style.background = 'rgba(1,13,24,0.78)';
    label.style.border = '1px solid rgba(255,255,255,0.22)';
    label.style.color = '#fff';
    label.style.fontSize = `${Math.max(10, Math.round(options.size * 0.58))}px`;
    label.style.fontWeight = '800';
    label.style.lineHeight = '1';
    label.style.whiteSpace = 'nowrap';
    wrap.appendChild(label);
  }

  return wrap;
}

function latLngToCoord(
  position: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined,
): { lat: number; lon: number } | null {
  if (!position) {
    return null;
  }

  if (typeof (position as google.maps.LatLng).lat === 'function') {
    const latLng = position as google.maps.LatLng;
    return { lat: latLng.lat(), lon: latLng.lng() };
  }

  const literal = position as google.maps.LatLngLiteral;
  return { lat: literal.lat, lon: literal.lng };
}

function addAdvancedMarker(
  map: google.maps.Map,
  overlays: MapOverlay[],
  position: google.maps.LatLngLiteral,
  options: {
    color: string;
    label?: string;
    size: number;
    selected?: boolean;
    zIndex?: number;
    markerId?: string;
    onClick?: (markerId: string) => void;
    draggable?: boolean;
    onDragEnd?: (markerId: string, coord: { lat: number; lon: number }) => void;
    forceClassic?: boolean;
    mapMarkerScale?: number;
    lightOutline?: boolean;
    icon?: 'video';
  },
) {
  const markerScale = options.mapMarkerScale ?? 1;
  const markerOutline = options.lightOutline === false ? options.color : '#FFFFFF';
  const isDraggable = Boolean(options.draggable && options.onDragEnd && options.markerId);
  const isInteractive = Boolean(isDraggable || (options.onClick && options.markerId));
  const useClassic = options.forceClassic || !canUseAdvancedMarkers();

  if (useClassic) {
    const symbolScale = Math.max(6, options.size / 2.2);
    const videoSvg =
      options.icon === 'video'
        ? `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${options.size + 8}" height="${options.size + 8}" viewBox="0 0 ${options.size + 8} ${options.size + 8}">
          <circle cx="${(options.size + 8) / 2}" cy="${(options.size + 8) / 2}" r="${options.size / 2}" fill="${options.color}" stroke="${markerOutline}" stroke-width="${options.selected ? 3 : 2.4}"/>
          <path d="M17 10.5V6c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-4.5l4 4v-11l-4 4ZM15 17H5V7h10v10Z" transform="translate(${(options.size + 8 - 18) / 2} ${(options.size + 8 - 18) / 2}) scale(0.75)" fill="#020A12"/>
        </svg>
      `)}`
        : null;
    const marker = new google.maps.Marker({
      map,
      position,
      draggable: isDraggable,
      clickable: isInteractive,
      icon: {
        ...(videoSvg
          ? {
              url: videoSvg,
              scaledSize: new google.maps.Size(options.size + 8, options.size + 8),
              anchor: new google.maps.Point((options.size + 8) / 2, (options.size + 8) / 2),
            }
          : {
              path: google.maps.SymbolPath.CIRCLE,
              scale: symbolScale,
              fillColor: options.color,
              fillOpacity: 1,
              strokeColor: markerOutline,
              strokeWeight: options.selected ? 3 : 2.4,
            }),
      },
      label: options.label
        ? {
            text: options.label,
            color: '#FFFFFF',
            fontSize: `${Math.max(12, Math.round(12 * markerScale))}px`,
            fontWeight: '800',
          }
        : undefined,
      zIndex: options.zIndex,
      cursor: isDraggable ? 'grab' : options.onClick && options.markerId ? 'pointer' : undefined,
    });
    if (options.onClick && options.markerId) {
      marker.addListener('click', (event: google.maps.MapMouseEvent) => {
        event.stop();
        options.onClick?.(options.markerId!);
      });
    }
    if (isDraggable) {
      marker.addListener('dragend', () => {
        const coord = latLngToCoord(marker.getPosition());
        if (coord) {
          options.onDragEnd?.(options.markerId!, coord);
        }
      });
    }
    overlays.push(marker);
    return;
  }

  const content = advancedMarkerContent(options);
  if (!isInteractive) {
    content.style.pointerEvents = 'none';
  } else if (isDraggable) {
    content.style.cursor = 'grab';
  } else if (options.onClick && options.markerId) {
    content.style.cursor = 'pointer';
    content.addEventListener('click', (event) => {
      event.stopPropagation();
      options.onClick?.(options.markerId!);
    });
  }

  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position,
    content,
    zIndex: options.zIndex,
    gmpDraggable: isDraggable,
  });

  if (options.onClick && options.markerId && isDraggable) {
    marker.addListener('click', (event: google.maps.MapMouseEvent) => {
      event.stop();
      options.onClick?.(options.markerId!);
    });
  }

  if (isDraggable) {
    marker.addListener('dragend', () => {
      const coord = latLngToCoord(
        marker.position as google.maps.LatLng | google.maps.LatLngLiteral | null,
      );
      if (coord) {
        options.onDragEnd?.(options.markerId!, coord);
      }
    });
  }

  overlays.push(marker);
}

function replayCursorSymbol(color: string, scale = 9.5): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale,
    fillColor: color,
    fillOpacity: 1,
    strokeWeight: 0,
  };
}

function replayCursorMarkerContent(cursor: MapReplayCursor) {
  const dotSize = cursor.follow ? 30 : 28;
  const wrap = document.createElement('div');
  wrap.style.transform = 'translate(-50%, -50%)';
  wrap.style.pointerEvents = 'none';

  if (cursor.avatarUrl) {
    const frame = document.createElement('span');
    frame.style.display = 'block';
    frame.style.width = `${dotSize}px`;
    frame.style.height = `${dotSize}px`;
    frame.style.borderRadius = '999px';
    frame.style.background = '#020A12';
    frame.style.border = `2px solid ${cursor.color}`;
    frame.style.borderStyle = cursor.identityVariant ? 'dashed' : 'solid';
    frame.style.boxShadow = '0 0 0 2px rgba(7,17,28,0.55), 0 5px 14px rgba(0,0,0,0.48)';
    frame.style.overflow = 'hidden';

    const image = document.createElement('img');
    image.src = cursor.avatarUrl;
    image.alt = '';
    image.style.display = 'block';
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.objectFit = 'cover';
    frame.appendChild(image);
    wrap.appendChild(frame);
  } else {
    const dot = document.createElement('span');
    dot.style.display = 'grid';
    dot.style.placeItems = 'center';
    dot.style.width = `${dotSize}px`;
    dot.style.height = `${dotSize}px`;
    dot.style.borderRadius = '999px';
    dot.style.background = '#020A12';
    dot.style.border = `2px ${cursor.identityVariant ? 'dashed' : 'solid'} ${cursor.color}`;
    dot.style.color = '#FFFFFF';
    dot.style.fontSize = '10px';
    dot.style.fontWeight = '900';
    dot.style.lineHeight = '1';
    dot.style.boxShadow = '0 0 0 1px rgba(7,17,28,0.55), 0 4px 12px rgba(0,0,0,0.45)';
    dot.textContent = cursor.initials ?? '??';
    wrap.appendChild(dot);
  }

  return wrap;
}

function createCursorMarker(
  map: google.maps.Map,
  cursor: MapReplayCursor,
  position: google.maps.LatLngLiteral,
): CursorMarker {
  const stackIndex = cursor.stackIndex ?? 0;

  if (canUseAdvancedMarkers()) {
    return new google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      content: replayCursorMarkerContent(cursor),
      zIndex: REPLAY_CURSOR_Z_INDEX + stackIndex,
    });
  }

  return new google.maps.Marker({
    map,
    position,
    icon: cursor.avatarUrl
      ? {
          url: cursor.avatarUrl,
          scaledSize: new google.maps.Size(cursor.follow ? 30 : 28, cursor.follow ? 30 : 28),
          anchor: new google.maps.Point(
            (cursor.follow ? 30 : 28) / 2,
            (cursor.follow ? 30 : 28) / 2,
          ),
        }
      : replayCursorSymbol(cursor.color, cursor.follow ? 8 : 7),
    label: cursor.avatarUrl
      ? undefined
      : {
          text: cursor.initials ?? '??',
          color: '#FFFFFF',
          fontSize: '10px',
          fontWeight: '900',
        },
    zIndex: REPLAY_CURSOR_Z_INDEX + stackIndex,
  });
}

function updateCursorMarkerPosition(marker: CursorMarker, position: google.maps.LatLngLiteral) {
  if ('setPosition' in marker && typeof marker.setPosition === 'function') {
    marker.setPosition(position);
    return;
  }

  (marker as AdvancedMarker).position = position;
}

function updateCursorMarker(
  marker: CursorMarker,
  cursor: MapReplayCursor,
  position: google.maps.LatLngLiteral,
  positionOnly = false,
) {
  if (positionOnly) {
    updateCursorMarkerPosition(marker, position);
    return;
  }

  const stackIndex = cursor.stackIndex ?? 0;

  if ('setPosition' in marker && typeof marker.setPosition === 'function') {
    marker.setPosition(position);
    marker.setIcon(
      cursor.avatarUrl
        ? {
            url: cursor.avatarUrl,
            scaledSize: new google.maps.Size(cursor.follow ? 30 : 28, cursor.follow ? 30 : 28),
            anchor: new google.maps.Point(
              (cursor.follow ? 30 : 28) / 2,
              (cursor.follow ? 30 : 28) / 2,
            ),
          }
        : replayCursorSymbol(cursor.color, cursor.follow ? 8 : 7),
    );
    marker.setLabel(
      cursor.avatarUrl
        ? null
        : {
            text: cursor.initials ?? '??',
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: '900',
          },
    );
    marker.setZIndex(REPLAY_CURSOR_Z_INDEX + stackIndex);
    return;
  }

  const advancedMarker = marker as AdvancedMarker;
  advancedMarker.position = position;
  advancedMarker.content = replayCursorMarkerContent(cursor);
  advancedMarker.zIndex = REPLAY_CURSOR_Z_INDEX + stackIndex;
}

function addDirectionArrowsAlongPath(
  map: google.maps.Map,
  overlays: MapOverlay[],
  path: google.maps.LatLngLiteral[],
  options: {
    color: string;
    outlineColor?: string;
    repeatPx?: number;
    scale?: number;
    zIndex?: number;
  },
) {
  if (path.length < 2) {
    return;
  }

  const repeatPx = options.repeatPx ?? 64;
  const scale = options.scale ?? 3.2;
  const zIndex = options.zIndex ?? 3;

  addPolyline(map, overlays, path, {
    strokeOpacity: 0,
    strokeWeight: 0,
    geodesic: true,
    zIndex,
    icons: [
      {
        icon: {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale,
          strokeColor: options.outlineColor ?? '#4A044E',
          strokeWeight: 1.2,
          fillColor: options.color,
          fillOpacity: 1,
          strokeOpacity: 1,
        },
        offset: `${Math.round(repeatPx / 3)}px`,
        repeat: `${repeatPx}px`,
      },
    ],
  });
}

const DEFAULT_REFERENCE_GUIDE_COLORS: ReferenceTrackGuideColors = {
  line: '#E879F9',
  arrow: '#F0ABFC',
  arrowOutline: '#701A75',
};

const DEFAULT_CIRCUIT_PREVIEW_LAYERS: TrackCatalogMapLayers = {
  trackOutline: true,
  corners: true,
  straights: true,
  sectors: true,
  lines: true,
  labels: false,
};

function contributionPaths(paths: TrackContributionGpsCoord[][]) {
  return paths.map((path) => path.map(({ lat, lon }) => ({ lat, lng: lon })));
}

function drawCorridorOutlineRings(
  map: google.maps.Map,
  overlays: MapOverlay[],
  rings: TrackContributionGpsCoord[][],
) {
  for (const ring of rings) {
    if (ring.length < 3) {
      continue;
    }

    overlays.push(
      new google.maps.Polygon({
        map,
        paths: contributionPaths([ring]),
        fillColor: '#ffffff',
        fillOpacity: 0.05,
        strokeColor: '#ffffff',
        strokeOpacity: 0.85,
        strokeWeight: 2,
        clickable: false,
        zIndex: 1,
      }),
    );
  }
}

function drawDetailedCircuitPreview(
  map: google.maps.Map,
  overlays: MapOverlay[],
  geometry: DetailedCircuitGeometry,
  layers: TrackCatalogMapLayers = DEFAULT_CIRCUIT_PREVIEW_LAYERS,
) {
  if (layers.trackOutline && geometry.outlineRings.length > 0) {
    for (const ring of geometry.outlineRings) {
      if (ring.length < 3) {
        continue;
      }
      overlays.push(
        new google.maps.Polygon({
          map,
          paths: contributionPaths([ring]),
          fillColor: '#ffffff',
          fillOpacity: 0.04,
          strokeColor: '#ffffff',
          strokeOpacity: 0.85,
          strokeWeight: 2,
          clickable: false,
          zIndex: 0,
        }),
      );
    }
  }

  const orderedFeatures = [...geometry.features].sort((left, right) => {
    const rank = (type: string) => (type.toLowerCase() === 'sector' ? 0 : 1);
    return rank(left.type) - rank(right.type);
  });

  for (const feature of orderedFeatures) {
    if (!trackCatalogSegmentVisible(feature.type, layers)) {
      continue;
    }

    const paths = feature.polygonRings ?? [];
    if (paths.length === 0) {
      continue;
    }

    const color = trackCatalogSegmentColor(feature.type);
    const isSector = feature.type.toLowerCase() === 'sector';
    overlays.push(
      new google.maps.Polygon({
        map,
        paths: contributionPaths(paths),
        fillColor: color,
        fillOpacity: isSector ? 0.11 : 0.34,
        strokeColor: color,
        strokeOpacity: isSector ? 0.6 : 0.95,
        strokeWeight: isSector ? 1.5 : 2,
        clickable: false,
        zIndex: isSector ? 1 : 3,
      }),
    );
  }

  if (geometry.centerLine && geometry.centerLine.length > 1) {
    addPolyline(map, overlays, contributionPaths([geometry.centerLine])[0]!, {
      strokeColor: '#28c8ff',
      strokeOpacity: 0.9,
      strokeWeight: 3,
      geodesic: true,
      zIndex: 4,
    });
  }
}

function drawReferenceTrackOutlines(
  map: google.maps.Map,
  overlays: MapOverlay[],
  referenceTrackGroups: TrackMapPoint[][],
  style: 'default' | 'guide' = 'default',
  guideColors: ReferenceTrackGuideColors[] = [],
  guideArrows = true,
) {
  for (let groupIndex = 0; groupIndex < referenceTrackGroups.length; groupIndex += 1) {
    const group = referenceTrackGroups[groupIndex]!;
    const trackPath = trackPathFromPoints(group);
    if (trackPath.length < 2) {
      continue;
    }

    if (style === 'guide') {
      const colors = guideColors[groupIndex] ?? DEFAULT_REFERENCE_GUIDE_COLORS;
      addPolyline(map, overlays, trackPath, {
        strokeColor: colors.line,
        strokeOpacity: 0,
        strokeWeight: 3,
        geodesic: true,
        zIndex: 2,
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeColor: colors.line,
              scale: 3,
            },
            offset: '0',
            repeat: '12px',
          },
        ],
      });
      if (guideArrows) {
        addDirectionArrowsAlongPath(map, overlays, trackPath, {
          color: colors.arrow,
          outlineColor: colors.arrowOutline,
          repeatPx: 72,
          scale: 3.4,
          zIndex: 4,
        });
      }
      continue;
    }

    addPolyline(map, overlays, trackPath, {
      strokeColor: '#07111C',
      strokeOpacity: 0.88,
      strokeWeight: 7,
      geodesic: true,
      zIndex: 0,
    });
    addPolyline(map, overlays, trackPath, {
      strokeColor: '#CBD5E1',
      strokeOpacity: 0.72,
      strokeWeight: 3.5,
      geodesic: true,
      zIndex: 1,
    });
  }
}

function drawTrackOverlays(
  map: google.maps.Map,
  overlays: MapOverlay[],
  trackGroups: TrackMapPoint[][],
  referenceTrackGroups: TrackMapPoint[][],
  coloredPaths: ColoredPath[],
  idealPath: google.maps.LatLngLiteral[],
  showIdealLine: boolean,
  showLapLines: boolean,
  channelOverlays: TrackMapChannelOverlay[],
  highlightLines: NonNullable<GoogleMapsTrackMapProps['highlightLines']>,
  points: TrackMapPoint[],
  sectorHighlightGroups: TrackMapPoint[][],
  selectedSectorIndex: number | null,
  mapMarkers: TrackMapMarker[],
  startFinishLine?: { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } },
  crossTrackLines: Array<{
    id: string;
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
    color?: string;
  }> = [],
  mapMarkerScale = 1,
  mapMarkerLightOutline = true,
  heatLineWidth = 4.5,
  heatLineHaloWidth = 9,
  heatLineWhiteHalo = true,
  onMapMarkerClick?: (markerId: string) => void,
  onMapMarkerDragEnd?: (markerId: string, coord: { lat: number; lon: number }) => void,
  referenceTrackStyle: 'default' | 'guide' = 'default',
  referenceTrackGuideColors: ReferenceTrackGuideColors[] = [],
  referenceTrackGuideArrows = true,
  detailedCircuitPreview?: DetailedCircuitGeometry | null,
  corridorOutlineRings: TrackContributionGpsCoord[][] = [],
) {
  overlays.forEach((overlay) => {
    clearMapOverlay(overlay);
  });
  overlays.length = 0;

  if (detailedCircuitPreview) {
    drawDetailedCircuitPreview(map, overlays, detailedCircuitPreview);
  } else if (corridorOutlineRings.length > 0) {
    drawCorridorOutlineRings(map, overlays, corridorOutlineRings);
  }

  drawReferenceTrackOutlines(
    map,
    overlays,
    referenceTrackGroups,
    referenceTrackStyle,
    referenceTrackGuideColors,
    referenceTrackGuideArrows,
  );

  if (showIdealLine && idealPath.length > 1) {
    addPolyline(map, overlays, idealPath, {
      strokeColor: '#07111C',
      strokeOpacity: 0.82,
      strokeWeight: 5.5,
      geodesic: true,
      zIndex: 2,
    });
    addPolyline(map, overlays, idealPath, {
      strokeColor: '#38BDF8',
      strokeOpacity: 0,
      strokeWeight: 0,
      geodesic: true,
      zIndex: 3,
      icons: [
        {
          icon: {
            path: 'M 0,-2 0,2',
            strokeOpacity: 1,
            strokeColor: '#38BDF8',
            scale: 2.2,
          },
          offset: '0',
          repeat: '11px',
        },
      ],
    });
  }

  const haloWidth = heatLineHaloWidth;
  const trackWidth = heatLineWidth;
  const idealActive = showIdealLine && idealPath.length > 1;
  const lapHaloZ = idealActive ? 4 : 1;
  const lapWhiteZ = idealActive ? 5 : 2;
  const lapColorZ = idealActive ? 6 : 4;

  if (showLapLines) {
    for (const group of trackGroups) {
      const trackPath = trackPathFromPoints(group);
      addPolyline(map, overlays, trackPath, {
        strokeColor: '#07111C',
        strokeOpacity: 0.92,
        strokeWeight: haloWidth,
        geodesic: true,
        zIndex: lapHaloZ,
      });

      if (heatLineWhiteHalo) {
        addPolyline(map, overlays, trackPath, {
          strokeColor: '#FFFFFF',
          strokeOpacity: 0.72,
          strokeWeight: haloWidth - 3,
          geodesic: true,
          zIndex: lapWhiteZ,
        });
      }
    }

    for (const coloredPath of coloredPaths) {
      addPolyline(map, overlays, coloredPath.path, {
        strokeColor: coloredPath.color,
        strokeOpacity: 1,
        strokeWeight: trackWidth,
        geodesic: true,
        zIndex: lapColorZ,
      });
    }
  }

  if (selectedSectorIndex !== null && sectorHighlightGroups[selectedSectorIndex]?.length) {
    const highlightPath = trackPathFromPoints(sectorHighlightGroups[selectedSectorIndex]!);
    addPolyline(map, overlays, highlightPath, {
      strokeColor: '#07111C',
      strokeOpacity: 0.95,
      strokeWeight: haloWidth + 3,
      zIndex: 6,
    });
    addPolyline(map, overlays, highlightPath, {
      strokeColor: '#A855F7',
      strokeOpacity: 0.95,
      strokeWeight: trackWidth + 2,
      zIndex: 7,
    });
  }

  channelOverlays.forEach((overlay, overlayIndex) => {
    const segments = overlay.points.slice(1).map((point, index) => {
      const previous = overlay.points[index]!;
      return {
        start: latLngFromPoint(previous),
        end: latLngFromPoint(point),
        color: point.heatColor,
      };
    });

    for (const coloredPath of mergeColoredPaths(segments)) {
      addPolyline(map, overlays, coloredPath.path, {
        strokeColor: coloredPath.color,
        strokeOpacity: 0.82,
        strokeWeight: 4.5,
        zIndex: 8 + overlayIndex,
      });
    }
  });

  for (const line of highlightLines) {
    addPolyline(
      map,
      overlays,
      line.points.map((point) => ({ lat: point.lat, lng: point.lon })),
      {
        strokeColor: line.color ?? '#F97316',
        strokeOpacity: line.opacity ?? 0.72,
        strokeWeight: line.width ?? 11,
        geodesic: false,
        zIndex: 14,
        icons: line.dash
          ? [
              {
                icon: {
                  path: 'M 0,-2 0,2',
                  strokeOpacity: 1,
                  strokeColor: line.color ?? '#F97316',
                  scale: 2,
                },
                offset: '0',
                repeat: `${Math.max(
                  4,
                  line.dash.reduce((sum, value) => sum + value, 0),
                )}px`,
              },
            ]
          : undefined,
      },
    );
  }

  if (startFinishLine) {
    addPolyline(
      map,
      overlays,
      [
        { lat: startFinishLine.pointA.lat, lng: startFinishLine.pointA.lon },
        { lat: startFinishLine.pointB.lat, lng: startFinishLine.pointB.lon },
      ],
      {
        strokeColor: '#FFFFFF',
        strokeOpacity: 0.95,
        strokeWeight: 10,
        zIndex: 19,
      },
    );
    addPolyline(
      map,
      overlays,
      [
        { lat: startFinishLine.pointA.lat, lng: startFinishLine.pointA.lon },
        { lat: startFinishLine.pointB.lat, lng: startFinishLine.pointB.lon },
      ],
      {
        strokeColor: '#22C55E',
        strokeOpacity: 1,
        strokeWeight: 6,
        zIndex: 20,
      },
    );
  }

  for (const line of crossTrackLines) {
    addPolyline(
      map,
      overlays,
      [
        { lat: line.pointA.lat, lng: line.pointA.lon },
        { lat: line.pointB.lat, lng: line.pointB.lon },
      ],
      {
        strokeColor: '#FFFFFF',
        strokeOpacity: 0.92,
        strokeWeight: 9,
        zIndex: 17,
      },
    );
    addPolyline(
      map,
      overlays,
      [
        { lat: line.pointA.lat, lng: line.pointA.lon },
        { lat: line.pointB.lat, lng: line.pointB.lon },
      ],
      {
        strokeColor: line.color ?? '#28c8ff',
        strokeOpacity: 1,
        strokeWeight: 5,
        zIndex: 18,
      },
    );
  }

  const start = points[0];
  const finish = points.at(-1);
  if (start) {
    addAdvancedMarker(
      map,
      overlays,
      { lat: start.lat, lng: start.lon },
      { color: '#22C55E', size: 12, zIndex: 5 },
    );
  }
  if (finish) {
    addAdvancedMarker(
      map,
      overlays,
      { lat: finish.lat, lng: finish.lon },
      { color: '#F59E0B', size: 12, zIndex: 5 },
    );
  }

  for (const marker of mapMarkers) {
    const baseSize =
      marker.kind === 'braking-soft' || marker.kind === 'braking-hard'
        ? 7
        : marker.kind === 'note'
          ? 24
          : marker.kind === 'footage-start'
            ? 18
            : marker.kind === 'sector'
              ? marker.selected
                ? 19
                : 15
              : marker.selected
                ? 16
                : 11;
    const size = baseSize * mapMarkerScale;
    addAdvancedMarker(
      map,
      overlays,
      { lat: marker.lat, lng: marker.lon },
      {
        color: marker.color,
        label:
          marker.kind !== 'sector-boundary'
            ? marker.label
            : marker.selected
              ? marker.label
              : undefined,
        selected: marker.selected,
        size,
        zIndex:
          marker.kind === 'braking-soft' || marker.kind === 'braking-hard'
            ? 16
            : marker.kind === 'note'
            ? 24
            : marker.kind === 'footage-start'
              ? 23
              : marker.selected
                ? 13
                : 11,
        markerId: marker.id,
        onClick:
          marker.kind === 'note' || marker.kind === 'footage-start' ? onMapMarkerClick : undefined,
        draggable:
          (marker.kind === 'note' || marker.kind === 'section-boundary') &&
          Boolean(onMapMarkerDragEnd),
        onDragEnd:
          marker.kind === 'note' || marker.kind === 'section-boundary'
            ? onMapMarkerDragEnd
            : undefined,
        forceClassic:
          marker.kind === 'note' ||
          marker.kind === 'footage-start' ||
          marker.kind === 'section-boundary',
        mapMarkerScale,
        lightOutline: marker.kind === 'footage-start' ? false : mapMarkerLightOutline,
        icon: marker.kind === 'footage-start' ? 'video' : undefined,
      },
    );
  }
}

const FOLLOW_ZOOM = 19;
const LAP_BOUNDS_PADDING = 68;
const CENTER_EPSILON = 1e-7;

function applyZoomAfterFit(
  map: google.maps.Map,
  maxFitZoom: number,
  minFitZoom: number | undefined,
  applyingProgrammaticViewRef: { current: boolean },
) {
  applyingProgrammaticViewRef.current = true;
  google.maps.event.addListenerOnce(map, 'idle', () => {
    let zoom = map.getZoom();
    if (zoom === undefined) {
      applyingProgrammaticViewRef.current = false;
      return;
    }
    if (zoom > maxFitZoom) {
      zoom = maxFitZoom;
    }
    if (minFitZoom !== undefined && zoom < minFitZoom) {
      zoom = minFitZoom;
    }
    if (zoom !== map.getZoom()) {
      map.setZoom(zoom);
    }
    applyingProgrammaticViewRef.current = false;
  });
}

function centersDiffer(
  left: google.maps.LatLngLiteral,
  right: google.maps.LatLngLiteral,
  epsilon = CENTER_EPSILON,
) {
  return Math.abs(left.lat - right.lat) > epsilon || Math.abs(left.lng - right.lng) > epsilon;
}

function trackShell(props: {
  fill?: boolean;
  height: number;
  title?: string;
  showLegend: boolean;
  legendTrackItems?: TrackMapLegendItem[];
  showIdealLine?: boolean;
  children: ReactNode;
}) {
  const {
    fill,
    height,
    title,
    showLegend,
    legendTrackItems = [],
    showIdealLine = false,
    children,
  } = props;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: fill ? '100%' : height,
        minHeight: fill ? 260 : height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        px: fill ? 0 : 1,
        py: fill ? 0 : 0,
        borderRadius: title ? '6px' : undefined,
        border: title ? '1px solid rgba(36,62,82,0.78)' : undefined,
        bgcolor: title ? 'rgba(1,13,24,0.46)' : undefined,
        overflow: 'hidden',
      }}
    >
      {title ? (
        <Typography sx={{ flexShrink: 0, px: 0.5, pb: 0.5, fontSize: '0.78rem', fontWeight: 850 }}>
          {title}
        </Typography>
      ) : null}

      {children}

      {showLegend ? (
        <TrackMapLegend trackItems={legendTrackItems} showIdealLine={showIdealLine} />
      ) : null}
    </Box>
  );
}

export function GoogleMapsTrackMap({
  apiKey,
  points,
  trackGroups,
  referenceTrackGroups = [],
  speedRange,
  idealPoints = [],
  showIdealLine = false,
  showLapLines = true,
  channelOverlays = [],
  height = 292,
  title,
  showLegend = true,
  legendTrackItems = [],
  fill = false,
  cursorTimecodeMs = null,
  replayCursors,
  followReplayCursor = false,
  resolveReplayCursorTimecodeMsRef,
  resetViewKey,
  onMapClick,
  onMapLatLngClick,
  onMapLatLngHover,
  highlightLines = [],
  freehandDrawActive = false,
  onMapLatLngDrawStroke,
  onMapMarkerClick,
  onMapMarkerDragEnd,
  mapHoverMarker,
  startFinishLine,
  crossTrackLines = [],
  mapMarkers = [],
  mapMarkerScale = 1,
  mapMarkerLightOutline = true,
  heatLineWidth = 4.5,
  heatLineHaloWidth = 9,
  heatLineWhiteHalo = true,
  fitBoundsPadding = LAP_BOUNDS_PADDING,
  maxFitZoom = FOLLOW_ZOOM,
  minFitZoom,
  sectorHighlightGroups = [],
  selectedSectorIndex = null,
  onTilesReady,
  mapViewport,
  autoFitBounds = true,
  referenceTrackStyle = 'default',
  referenceTrackGuideColors = [],
  referenceTrackGuideArrows = true,
  detailedCircuitPreview = null,
  corridorOutlineRings = [],
}: GoogleMapsTrackMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<MapOverlay[]>([]);
  const cursorMarkersRef = useRef<Map<string, CursorMarker>>(new Map());
  const cursorInterpHintsRef = useRef<Map<string, InterpolateMapPointHint>>(new Map());
  const replayMotionCachesRef = useRef<
    Map<string, { points: TrackMapPoint[]; cache: ReplayMotionCache }>
  >(new Map());
  const hoverMarkerRef = useRef<google.maps.Marker | null>(null);
  const hoverMarkerClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const pointsRef = useRef(points);
  const sessionFitKeyRef = useRef('');
  const lapFitKeyRef = useRef('');
  const cursorTimecodeMsRef = useRef(cursorTimecodeMs);
  const replayCursorsRef = useRef<MapReplayCursor[]>([]);
  const resolveReplayCursorTimecodeMsRefRef = useRef(resolveReplayCursorTimecodeMsRef);
  const overlayFingerprintRef = useRef('');
  const resetViewKeyRef = useRef<string | number | undefined>(resetViewKey);
  const userViewLockedRef = useRef(false);
  const applyingProgrammaticViewRef = useRef(false);
  const onTilesReadyRef = useRef(onTilesReady);
  const onMapMarkerClickRef = useRef(onMapMarkerClick);
  const onMapMarkerDragEndRef = useRef(onMapMarkerDragEnd);
  const onMapLatLngClickRef = useRef(onMapLatLngClick);
  const onMapLatLngHoverRef = useRef(onMapLatLngHover);
  const onMapLatLngDrawStrokeRef = useRef(onMapLatLngDrawStroke);
  const freehandDrawActiveRef = useRef(freehandDrawActive);
  const mapViewportRef = useRef(mapViewport);
  const [mapReady, setMapReady] = useState(false);
  const [tilesReady, setTilesReady] = useState(false);
  const [mapAuthFailed, setMapAuthFailed] = useState(mapsAuthHasFailed());

  useEffect(() => {
    registerGoogleMapsAuthFailureHandler();
    return onMapsAuthFailure(() => setMapAuthFailed(true));
  }, []);

  const resolvedCursors = useMemo((): MapReplayCursor[] => {
    if (replayCursors?.length) {
      return replayCursors;
    }

    if (cursorTimecodeMs !== null && points.length >= 2) {
      return [
        {
          id: 'primary',
          points,
          timecodeMs: cursorTimecodeMs,
          color: '#A855F7',
          follow: true,
        },
      ];
    }

    return [];
  }, [cursorTimecodeMs, points, replayCursors]);

  const resolvedTrackGroups = useMemo(
    () =>
      (trackGroups?.length ? trackGroups : points.length >= 2 ? [points] : []).flatMap((group) =>
        splitContinuousTelemetryPoints(group),
      ),
    [points, trackGroups],
  );
  const resolvedReferenceTrackGroups = useMemo(
    () => referenceTrackGroups.filter((group) => group.length >= 2),
    [referenceTrackGroups],
  );
  const resolvedReferenceTrackGuideColors = useMemo(
    () => referenceTrackGuideColors.slice(0, resolvedReferenceTrackGroups.length),
    [referenceTrackGuideColors, resolvedReferenceTrackGroups.length],
  );
  const coloredPaths = useMemo(
    () =>
      resolvedTrackGroups.flatMap((group) => {
        const groupRange = speedRange ?? trackSpeedRange(group) ?? undefined;
        return mergeColoredPaths(latLngSegments(group, groupRange));
      }),
    [resolvedTrackGroups, speedRange],
  );
  const idealPath = useMemo(() => trackPathFromPoints(idealPoints), [idealPoints]);
  const legendHeight = showLegend ? 28 : 0;
  const titleHeight = title ? 34 : 0;

  pointsRef.current = points;
  cursorTimecodeMsRef.current = cursorTimecodeMs;
  replayCursorsRef.current = resolvedCursors;
  resolveReplayCursorTimecodeMsRefRef.current = resolveReplayCursorTimecodeMsRef;

  onTilesReadyRef.current = onTilesReady;
  onMapMarkerClickRef.current = onMapMarkerClick;
  onMapMarkerDragEndRef.current = onMapMarkerDragEnd;
  onMapLatLngClickRef.current = onMapLatLngClick;
  onMapLatLngHoverRef.current = onMapLatLngHover;
  onMapLatLngDrawStrokeRef.current = onMapLatLngDrawStroke;
  freehandDrawActiveRef.current = freehandDrawActive;
  mapViewportRef.current = mapViewport;

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let cancelled = false;

    void loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }

        if (mapsAuthHasFailed()) {
          setMapAuthFailed(true);
          return;
        }

        if (!mapRef.current) {
          const start =
            pointsRef.current[0] ??
            (mapViewportRef.current
              ? { lat: mapViewportRef.current.lat, lon: mapViewportRef.current.lon }
              : null);
          if (!start) {
            return;
          }

          // Satellite imagery must not use a vector cloud mapId — that yields a blank map.
          mapRef.current = new google.maps.Map(containerRef.current, {
            mapTypeId: 'satellite',
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'cooperative',
            backgroundColor: '#020A12',
            clickableIcons: false,
            isFractionalZoomEnabled: true,
            maxZoom: 21,
            center: { lat: start.lat, lng: start.lon },
            zoom: mapViewportRef.current?.zoom ?? FOLLOW_ZOOM,
          });

          const markTilesReady = () => {
            if (cancelled) {
              return;
            }

            setMapAuthFailed(false);
            setTilesReady(true);
            onTilesReadyRef.current?.();
          };

          google.maps.event.addListenerOnce(mapRef.current, 'tilesloaded', markTilesReady);
          google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
            window.setTimeout(markTilesReady, 300);
          });

          setMapReady(true);
          return;
        }

        setMapReady(true);
      })
      .catch(() => {
        if (!cancelled && mapsAuthHasFailed()) {
          setMapAuthFailed(true);
        }
      });

    return () => {
      cancelled = true;
      overlaysRef.current.forEach((overlay) => {
        clearMapOverlay(overlay);
      });
      overlaysRef.current = [];
      for (const marker of cursorMarkersRef.current.values()) {
        clearMapOverlay(marker);
      }
      cursorMarkersRef.current.clear();
      hoverMarkerClickListenerRef.current?.remove();
      hoverMarkerClickListenerRef.current = null;
      hoverMarkerRef.current?.setMap(null);
      hoverMarkerRef.current = null;
      mapRef.current = null;
      sessionFitKeyRef.current = '';
      lapFitKeyRef.current = '';
      overlayFingerprintRef.current = '';
      resetViewKeyRef.current = undefined;
      setMapReady(false);
      setTilesReady(false);
    };
  }, [apiKey]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !mapViewport) {
      return;
    }

    applyingProgrammaticViewRef.current = true;
    map.moveCamera({
      center: { lat: mapViewport.lat, lng: mapViewport.lon },
      zoom: mapViewport.zoom ?? map.getZoom() ?? FOLLOW_ZOOM,
    });
    window.setTimeout(() => {
      applyingProgrammaticViewRef.current = false;
    }, 120);
  }, [mapReady, mapViewport?.lat, mapViewport?.lon, mapViewport?.zoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const listener = map.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (freehandDrawActiveRef.current) {
        return;
      }
      const latLng = event.latLng;
      if (latLng && onMapLatLngClickRef.current) {
        onMapLatLngClickRef.current({ lat: latLng.lat(), lon: latLng.lng() });
        return;
      }
      onMapClick?.();
    });
    return () => {
      listener?.remove();
    };
  }, [mapReady, onMapClick]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !freehandDrawActive) {
      return;
    }

    let drawing = false;

    const stopDrawing = () => {
      if (!drawing) {
        return;
      }
      drawing = false;
      map.setOptions({ draggable: true, draggableCursor: 'crosshair' });
    };

    const strokeAt = (event: google.maps.MapMouseEvent) => {
      const latLng = event.latLng;
      if (!latLng) {
        return;
      }
      onMapLatLngDrawStrokeRef.current?.({ lat: latLng.lat(), lon: latLng.lng() });
    };

    const downListener = map.addListener('mousedown', (event: google.maps.MapMouseEvent) => {
      if ((event.domEvent as MouseEvent | undefined)?.button !== 0) {
        return;
      }
      drawing = true;
      map.setOptions({ draggable: false, draggableCursor: 'crosshair' });
      strokeAt(event);
    });
    const moveListener = map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
      if (!drawing) {
        return;
      }
      strokeAt(event);
    });
    const upListener = map.addListener('mouseup', stopDrawing);
    const dragEndListener = map.addListener('dragend', stopDrawing);

    map.setOptions({ draggableCursor: 'crosshair' });

    return () => {
      downListener.remove();
      moveListener.remove();
      upListener.remove();
      dragEndListener.remove();
      map.setOptions({ draggable: true, draggableCursor: undefined });
    };
  }, [freehandDrawActive, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const moveListener = map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
      const latLng = event.latLng;
      if (latLng) {
        onMapLatLngHoverRef.current?.({ lat: latLng.lat(), lon: latLng.lng() });
      }
    });
    const outListener = map.addListener('mouseout', () => {
      onMapLatLngHoverRef.current?.(null);
    });
    return () => {
      moveListener?.remove();
      outListener?.remove();
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !mapHoverMarker) {
      hoverMarkerClickListenerRef.current?.remove();
      hoverMarkerClickListenerRef.current = null;
      hoverMarkerRef.current?.setMap(null);
      hoverMarkerRef.current = null;
      map?.setOptions({ draggableCursor: undefined });
      return;
    }

    map.setOptions({ draggableCursor: 'crosshair' });
    if (!hoverMarkerRef.current) {
      hoverMarkerRef.current = new google.maps.Marker({
        map,
        clickable: true,
        cursor: 'crosshair',
        zIndex: 100,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: '#F59E0B',
          fillOpacity: 0.55,
          strokeColor: '#FFFFFF',
          strokeOpacity: 1,
          strokeWeight: 2.5,
        },
        label: {
          text: '+',
          color: '#FFFFFF',
          fontSize: '16px',
          fontWeight: '900',
        },
      });
      hoverMarkerClickListenerRef.current = hoverMarkerRef.current.addListener('click', () => {
        const coord = latLngToCoord(hoverMarkerRef.current?.getPosition());
        if (coord) {
          onMapLatLngClickRef.current?.(coord);
        }
      });
    }
    hoverMarkerRef.current.setPosition({ lat: mapHoverMarker.lat, lng: mapHoverMarker.lon });
  }, [mapHoverMarker?.lat, mapHoverMarker?.lon, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const dragListener = map.addListener('dragstart', () => {
      userViewLockedRef.current = true;
    });
    const zoomListener = map.addListener('zoom_changed', () => {
      if (!applyingProgrammaticViewRef.current) {
        userViewLockedRef.current = true;
      }
    });
    return () => {
      dragListener?.remove();
      zoomListener?.remove();
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const hasTrackPoints =
      points.length >= 2 || resolvedTrackGroups.some((group) => group.length >= 2);
    const hasReferenceTracks = resolvedReferenceTrackGroups.some((group) => group.length >= 2);
    const hasAnnotations = mapMarkers.length > 0 || Boolean(startFinishLine);

    if (!hasTrackPoints && !hasAnnotations && !hasReferenceTracks) {
      return;
    }

    const first = points[0];
    const last = points.at(-1);
    const fingerprint = [
      resolvedTrackGroups.length,
      resolvedReferenceTrackGroups.length,
      referenceTrackStyle,
      resolvedReferenceTrackGuideColors.map((colors) => colors.line).join('|'),
      points.length,
      first?.timecodeMs ?? 0,
      last?.timecodeMs ?? 0,
      showLapLines ? 1 : 0,
      showIdealLine ? 1 : 0,
      idealPath.length,
      channelOverlays.map((overlay) => `${overlay.id}:${overlay.points.length}`).join('|'),
      highlightLines
        .map(
          (line) =>
            `${line.id}:${line.points.length}:${line.color}:${line.width}:${line.opacity}:${line.dash?.join(',')}`,
        )
        .join('|'),
      coloredPaths.length,
      selectedSectorIndex ?? -1,
      sectorHighlightGroups.length,
      mapMarkers
        .map(
          (marker) =>
            `${marker.id}:${marker.kind}:${marker.label}:${marker.color}:${marker.lat.toFixed(5)}:${marker.lon.toFixed(5)}:${marker.selected ? 1 : 0}`,
        )
        .join('|'),
      Boolean(onMapMarkerClick) ? 1 : 0,
      Boolean(onMapMarkerDragEnd) ? 1 : 0,
      startFinishLine
        ? `${startFinishLine.pointA.lat}:${startFinishLine.pointA.lon}:${startFinishLine.pointB.lat}:${startFinishLine.pointB.lon}`
        : '',
    ].join(':');

    const viewResetRequested = resetViewKeyRef.current !== resetViewKey;
    if (viewResetRequested) {
      resetViewKeyRef.current = resetViewKey;
      sessionFitKeyRef.current = '';
      lapFitKeyRef.current = '';
      userViewLockedRef.current = false;
    }

    const shouldAutoFit = autoFitBounds && (!userViewLockedRef.current || viewResetRequested);

    if (overlayFingerprintRef.current === fingerprint && !viewResetRequested) {
      return;
    }

    overlayFingerprintRef.current = fingerprint;

    drawTrackOverlays(
      map,
      overlaysRef.current,
      resolvedTrackGroups,
      resolvedReferenceTrackGroups,
      coloredPaths,
      idealPath,
      showIdealLine,
      showLapLines,
      channelOverlays,
      highlightLines,
      points,
      sectorHighlightGroups,
      selectedSectorIndex,
      mapMarkers,
      startFinishLine,
      crossTrackLines,
      mapMarkerScale,
      mapMarkerLightOutline,
      heatLineWidth,
      heatLineHaloWidth,
      heatLineWhiteHalo,
      onMapMarkerClickRef.current
        ? (markerId) => onMapMarkerClickRef.current?.(markerId)
        : undefined,
      onMapMarkerDragEndRef.current
        ? (markerId, coord) => onMapMarkerDragEndRef.current?.(markerId, coord)
        : undefined,
      referenceTrackStyle,
      resolvedReferenceTrackGuideColors,
      referenceTrackGuideArrows,
      detailedCircuitPreview,
      corridorOutlineRings,
    );

    const fitKey = `${resolvedTrackGroups.length}:${points.length}:${first?.timecodeMs ?? 0}:${last?.timecodeMs ?? 0}`;
    if (
      shouldAutoFit &&
      resolvedTrackGroups.length > 1 &&
      (sessionFitKeyRef.current !== fitKey || viewResetRequested)
    ) {
      const bounds = new google.maps.LatLngBounds();
      for (const group of resolvedTrackGroups) {
        for (const point of group) {
          bounds.extend({ lat: point.lat, lng: point.lon });
        }
      }
      for (const group of resolvedReferenceTrackGroups) {
        for (const point of group) {
          bounds.extend({ lat: point.lat, lng: point.lon });
        }
      }
      extendBoundsWithMapMarkers(bounds, mapMarkers);
      applyingProgrammaticViewRef.current = true;
      map.fitBounds(bounds, fitBoundsPadding);
      sessionFitKeyRef.current = fitKey;
      applyZoomAfterFit(map, maxFitZoom, minFitZoom, applyingProgrammaticViewRef);
    } else if (shouldAutoFit && resolvedTrackGroups.length <= 1) {
      sessionFitKeyRef.current = '';
      const shouldFitLapOrReference = points.length >= 2 || hasReferenceTracks;
      if (shouldFitLapOrReference && (lapFitKeyRef.current !== fitKey || viewResetRequested)) {
        const bounds = new google.maps.LatLngBounds();
        for (const point of points) {
          bounds.extend({ lat: point.lat, lng: point.lon });
        }
        for (const group of resolvedReferenceTrackGroups) {
          for (const point of group) {
            bounds.extend({ lat: point.lat, lng: point.lon });
          }
        }
        extendBoundsWithMapMarkers(bounds, mapMarkers);
        applyingProgrammaticViewRef.current = true;
        map.fitBounds(bounds, fitBoundsPadding);
        lapFitKeyRef.current = fitKey;
        applyZoomAfterFit(map, maxFitZoom, minFitZoom, applyingProgrammaticViewRef);
      }
    }
  }, [
    autoFitBounds,
    coloredPaths,
    channelOverlays,
    fitBoundsPadding,
    idealPath,
    mapMarkers,
    mapReady,
    maxFitZoom,
    minFitZoom,
    points,
    resolvedReferenceTrackGroups,
    resolvedTrackGroups,
    resetViewKey,
    sectorHighlightGroups,
    selectedSectorIndex,
    showIdealLine,
    showLapLines,
    startFinishLine,
    crossTrackLines,
    mapMarkerScale,
    mapMarkerLightOutline,
    heatLineWidth,
    highlightLines,
    heatLineHaloWidth,
    heatLineWhiteHalo,
    onMapMarkerClick,
    onMapMarkerDragEnd,
    referenceTrackStyle,
    resolvedReferenceTrackGuideColors,
    referenceTrackGuideArrows,
    detailedCircuitPreview,
    corridorOutlineRings,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!mapReady || !map || !(container instanceof Element)) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (mapRef.current) {
        google.maps.event.trigger(mapRef.current, 'resize');
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [mapReady]);

  const replayActive = resolvedCursors.some((cursor) => cursor.timecodeMs !== null);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !containerRef.current) {
      return;
    }

    if (!replayActive) {
      for (const marker of cursorMarkersRef.current.values()) {
        clearMapOverlay(marker);
      }
      cursorMarkersRef.current.clear();
      cursorInterpHintsRef.current.clear();
      replayMotionCachesRef.current.clear();
      return;
    }

    const cursorIds = new Set(replayCursorsRef.current.map((cursor) => cursor.id));
    for (const id of cursorInterpHintsRef.current.keys()) {
      if (!cursorIds.has(id)) {
        cursorInterpHintsRef.current.delete(id);
      }
    }
    for (const id of replayMotionCachesRef.current.keys()) {
      if (!cursorIds.has(id)) {
        replayMotionCachesRef.current.delete(id);
      }
    }

    const updateCursors = () => {
      const activeMap = mapRef.current;
      const cursors = replayCursorsRef.current;

      if (!activeMap || cursors.length === 0) {
        return;
      }

      const activeIds = new Set<string>();

      for (const cursor of cursors) {
        const resolve = resolveReplayCursorTimecodeMsRefRef.current?.current;
        const timecodeMs = resolve?.(cursor) ?? cursor.timecodeMs;
        if (timecodeMs === null || cursor.points.length === 0) {
          continue;
        }

        activeIds.add(cursor.id);
        let cursorPoint: { lat: number; lon: number } | null = null;
        const cached = replayMotionCachesRef.current.get(cursor.id);
        if (cached && cached.points === cursor.points) {
          cursorPoint = sampleReplayMotionCache(cached.cache, timecodeMs);
        } else {
          const built = buildReplayMotionCache(cursor.points);
          if (built) {
            replayMotionCachesRef.current.set(cursor.id, { points: cursor.points, cache: built });
            cursorPoint = sampleReplayMotionCache(built, timecodeMs);
          }
        }

        if (!cursorPoint) {
          const hint = cursorInterpHintsRef.current.get(cursor.id) ?? { index: 0 };
          cursorInterpHintsRef.current.set(cursor.id, hint);
          const sample = interpolateMapPointAtTimeWithHint(cursor.points, timecodeMs, hint);
          if (!sample) {
            continue;
          }
          cursorPoint = { lat: sample.lat, lon: sample.lon };
        }

        let marker = cursorMarkersRef.current.get(cursor.id);
        if (!marker) {
          marker = createCursorMarker(activeMap, cursor, {
            lat: cursorPoint.lat,
            lng: cursorPoint.lon,
          });
          cursorMarkersRef.current.set(cursor.id, marker);
        }

        updateCursorMarker(marker, cursor, { lat: cursorPoint.lat, lng: cursorPoint.lon }, true);

        if (followReplayCursor && cursor.follow) {
          activeMap.setCenter({ lat: cursorPoint.lat, lng: cursorPoint.lon });
        }
      }

      for (const [id, marker] of cursorMarkersRef.current) {
        if (!activeIds.has(id)) {
          clearMapOverlay(marker);
          cursorMarkersRef.current.delete(id);
          cursorInterpHintsRef.current.delete(id);
        }
      }
    };

    if (followReplayCursor && resolvedTrackGroups.length <= 1) {
      applyingProgrammaticViewRef.current = true;
      map.setZoom(FOLLOW_ZOOM);
      google.maps.event.addListenerOnce(map, 'idle', () => {
        applyingProgrammaticViewRef.current = false;
      });
    }

    updateCursors();
    return subscribeToPlayheadTicks(updateCursors);
  }, [followReplayCursor, mapReady, replayActive, resolvedTrackGroups.length]);

  if (points.length < 2 && !mapViewport) {
    return trackShell({
      fill,
      height,
      title,
      showLegend,
      legendTrackItems,
      showIdealLine,
      children: (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#A6B0BE' }}>
            No GPS points for this lap
          </Typography>
        </Box>
      ),
    });
  }

  return trackShell({
    fill,
    height,
    title,
    showLegend,
    legendTrackItems,
    showIdealLine,
    children: (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          flex: fill ? 1 : undefined,
          height: fill ? '100%' : height - titleHeight - legendHeight - 8,
          minHeight: fill ? 0 : height - titleHeight - legendHeight - 8,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
          }}
        />
        {mapAuthFailed && !tilesReady ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              px: 2,
              textAlign: 'center',
              background: 'rgba(2,10,18,0.82)',
            }}
          >
            <Typography sx={{ fontSize: '0.82rem', color: '#F8FAFC', fontWeight: 700 }}>
              Google Maps could not load
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: '0.72rem', color: '#94A3B8', lineHeight: 1.45 }}>
              {mapsDevAuthHint(apiKey.slice(-6))}
            </Typography>
          </Box>
        ) : null}
        {tilesReady ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'linear-gradient(180deg, rgba(2,10,18,0.10), rgba(2,10,18,0.04) 34%, rgba(2,10,18,0.12))',
              boxShadow:
                'inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 0 90px rgba(2,10,18,0.22)',
            }}
          />
        ) : null}
      </Box>
    ),
  });
}
